/**
 * Google Ads tag: one page-view conversion for an arrival on the landing page,
 * so YouTube campaigns can count visits from voters who saw or clicked an ad.
 *
 * Same rules as the Meta Pixel: the production host only, the landing path,
 * outside the partner embed, not a `?address=` research link or a synthetic
 * monitoring run, and never under Global Privacy Control or after the voter
 * turns advertising partners off (Your Privacy Choices). In-app browsers stay
 * eligible, since YouTube opens ad clicks in its own.
 *
 * Called once from `index.tsx` before React mounts, so Strict Mode, the
 * `/` → `/chat` redirect and SPA navigation never repeat it. Only the Google
 * Ads ID is configured, with its automatic page view off, so the conversion is
 * the tag's one hit.
 */
import { isEmbed } from '@/config/embed';
import { getGoogleAdsTagHost } from '@/config/env';
import { isSyntheticSession } from '@/config/synthetic';
import { getCapturedResearchAddress } from './researchAddressBootstrap';
import { isGpcEnabled, whenAdConsentGranted } from './adConsentStore';
import { isReportableUrl } from './adPartnerUrl';

const ADS_ID = 'AW-17302944632';
const CONVERSION_SEND_TO = 'AW-17302944632/dsjoCKWZqaIbEPj217pA';
const SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
const LOADED_KEY = '__votemateGoogleAdsTag';

// Google's ad-click and attribution parameters, which the redirect to /chat carries along.
const GOOGLE_CLICK_KEYS: ReadonlySet<string> = new Set([
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
]);

export const isEligibleGoogleAdsArrival = ({
  hostname,
  allowedHost,
  pathname,
  embed,
  synthetic,
  hasResearchAddress,
  gpc,
}: {
  hostname: string;
  allowedHost: string | undefined;
  pathname: string;
  embed: boolean;
  synthetic: boolean;
  hasResearchAddress: boolean;
  gpc: boolean;
}): boolean =>
  !!allowedHost &&
  hostname.toLowerCase() === allowedHost &&
  pathname === '/' &&
  !embed &&
  !synthetic &&
  !hasResearchAddress &&
  !gpc;

// Google's base snippet: `gtag` queues its `arguments` on `dataLayer` until
// gtag.js loads and drains it. `page_location` pins the reported page (and any
// `gclid`) to the landing URL, which the router may have left by then.
const loadAndTrack = (landingHref: string): void => {
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  if (w.gtag) return;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // gtag.js only recognizes the `arguments` object, not an array.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);

  w.gtag('js', new Date());
  w.gtag('config', ADS_ID, { send_page_view: false, page_location: landingHref });
  w.gtag('event', 'conversion', { send_to: CONVERSION_SEND_TO, value: 1.0, currency: 'USD' });
};

export const loadGoogleAdsTag = (): void => {
  try {
    const w = window as unknown as Record<string, unknown>;
    if (w[LOADED_KEY]) return;
    w[LOADED_KEY] = true;

    const eligible = isEligibleGoogleAdsArrival({
      hostname: window.location.hostname,
      allowedHost: getGoogleAdsTagHost(),
      pathname: window.location.pathname,
      embed: isEmbed(),
      synthetic: isSyntheticSession(),
      hasResearchAddress: getCapturedResearchAddress() !== null,
      gpc: isGpcEnabled(),
    });
    if (!eligible) return;
    // gtag.js also reads the live URL, so a deferred conversion is sent only
    // from a URL without identifiers (see `isReportableUrl`).
    const landingHref = window.location.href;
    const run = () =>
      whenAdConsentGranted(
        () => {
          try {
            loadAndTrack(landingHref);
          } catch {
            // Tracking never interrupts the app.
          }
        },
        () => isReportableUrl(window.location.href, landingHref, GOOGLE_CLICK_KEYS)
      );

    // A prerendered page may never be opened, so wait until the visitor actually sees it.
    if ((document as Document & { prerendering?: boolean }).prerendering) {
      document.addEventListener('prerenderingchange', run, { once: true });
    } else {
      run();
    }
  } catch {
    // Tracking never interrupts the app.
  }
};
